export interface PortalConfig {
  apiUrl: string;
  region: string;
  userPoolId: string;
  clientId: string;
  cognitoDomain: string;
  version: string;
}

const REQUIRED: (keyof PortalConfig)[] = ["apiUrl", "region", "userPoolId", "clientId", "cognitoDomain"];

/** Returns the names of required settings that are missing or empty. */
export function missingSettings(config: Partial<PortalConfig>): string[] {
  return REQUIRED.filter((key) => !config[key]);
}

/**
 * Loads /config.json, which the deploy writes from the stack outputs (public IDs only, no
 * secrets). Local development reads public/config.local.json instead.
 */
export async function loadConfig(): Promise<PortalConfig> {
  const file = import.meta.env.DEV ? "/config.local.json" : "/config.json";
  const res = await fetch(file, { cache: "no-store" });
  if (!res.ok) throw new Error(`${file} returned ${res.status}`);
  const config = (await res.json()) as Partial<PortalConfig>;
  const missing = missingSettings(config);
  if (missing.length) throw new Error(`config.json is missing: ${missing.join(", ")}`);
  return config as PortalConfig;
}

/** OIDC settings for Cognito's managed login, using the authorization-code flow with PKCE. */
export function oidcSettings(config: PortalConfig, origin: string) {
  return {
    authority: `https://cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`,
    client_id: config.clientId,
    redirect_uri: `${origin}/`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: "code",
    scope: "openid email profile",
  };
}

/** Cognito's sign-out endpoint (it does not publish end_session_endpoint in its discovery document). */
export function signOutUrl(config: PortalConfig, origin: string): string {
  const params = new URLSearchParams({ client_id: config.clientId, logout_uri: `${origin}/` });
  return `https://${config.cognitoDomain}/logout?${params.toString()}`;
}
